// src/components/Admin/Leads/SalesLeadLocationPicker.jsx
//
// Up to 5 location filter rows for the Sales Call Leads page. Each row is one
// of: a specific place (city + radius, matched via ST_DWithin), National
// (whole country, no geo filter), or Remote (Lead.remote_service === true).
import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { X, Plus } from 'lucide-react';
import LocationSearch from '../SEO/LocationSearch';
import { toHexEwkbPoint } from '../../../utilities/geoPointUtils';

export const MAX_LOCATIONS = 5;
export const DEFAULT_RADIUS_KM = 30;

export function makeRadiusLocation({ lat, lng, radiusKm = DEFAULT_RADIUS_KM, label }) {
  return { scope_type: 'RADIUS', geo_point: toHexEwkbPoint(lat, lng), radius_km: radiusKm, label };
}

const SCOPE_LABELS = {
  RADIUS: 'Near a place',
  NATIONWIDE: 'National',
  REMOTE: 'Remote',
};

function LocationRow({ location, onChange, onRemove, countryCode }) {
  const setScopeType = (scope_type) => {
    if (scope_type === 'RADIUS') {
      onChange({ scope_type, geo_point: location.geo_point || null, radius_km: location.radius_km || DEFAULT_RADIUS_KM, label: location.label });
    } else {
      onChange({ scope_type, label: SCOPE_LABELS[scope_type] });
    }
  };

  return (
    <div className="flex items-start gap-2 rounded-md border border-border p-2">
      <Select value={location.scope_type} onValueChange={setScopeType}>
        <SelectTrigger className="h-9 w-36 text-sm shrink-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="RADIUS">Near a place</SelectItem>
          <SelectItem value="NATIONWIDE">National</SelectItem>
          <SelectItem value="REMOTE">Remote</SelectItem>
        </SelectContent>
      </Select>

      {location.scope_type === 'RADIUS' && (
        <div className="flex flex-1 flex-col gap-1 min-w-0">
          <LocationSearch
            countryCode={countryCode}
            placeholder={location.label || 'Search city...'}
            onSelect={(loc) => {
              const geo_point = toHexEwkbPoint(loc.latitude, loc.longitude);
              if (!geo_point) return;
              onChange({
                scope_type: 'RADIUS',
                geo_point,
                radius_km: location.radius_km || DEFAULT_RADIUS_KM,
                label: `${loc.city}, ${loc.state}`,
              });
            }}
          />
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground shrink-0">Radius (km)</span>
            <Input
              type="number"
              min={1}
              max={500}
              value={location.radius_km || DEFAULT_RADIUS_KM}
              onChange={(e) => onChange({ ...location, radius_km: Number(e.target.value) || DEFAULT_RADIUS_KM })}
              className="h-8 w-24 text-sm"
            />
            {location.label && (
              <span className="text-xs text-muted-foreground truncate">{location.label}</span>
            )}
          </div>
        </div>
      )}

      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={onRemove} aria-label="Remove location filter">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function SalesLeadLocationPicker({ locations, onChangeLocations, countryCode }) {
  const updateAt = (index, next) => {
    const copy = [...locations];
    copy[index] = next;
    onChangeLocations(copy);
  };

  const removeAt = (index) => {
    onChangeLocations(locations.filter((_, i) => i !== index));
  };

  const addLocation = () => {
    if (locations.length >= MAX_LOCATIONS) return;
    onChangeLocations([...locations, { scope_type: 'RADIUS', geo_point: null, radius_km: DEFAULT_RADIUS_KM, label: null }]);
  };

  return (
    <div className="flex flex-col gap-2">
      {locations.map((loc, i) => (
        <LocationRow
          key={i}
          location={loc}
          countryCode={countryCode}
          onChange={(next) => updateAt(i, next)}
          onRemove={() => removeAt(i)}
        />
      ))}
      {locations.length < MAX_LOCATIONS && (
        <Button type="button" variant="outline" size="sm" onClick={addLocation} className="w-fit gap-1">
          <Plus className="h-3.5 w-3.5" /> Add location ({locations.length}/{MAX_LOCATIONS})
        </Button>
      )}
    </div>
  );
}
